import type { TFunction } from "i18next";

// Stage and error CODES from /api/web/corrido/status and the mutation routes,
// translated on the page. The engine answers with codes, not sentences:
//   stage:      "generating_broll" | "animating" | "assembling" | "uploading" …
//               (services/corrido-v2/service.js STAGE_TEXT keys)
//   error_code: "E_SELFIE_COUNT" | "E_SUBMIT_FAILED" | "E_TIMEOUT" …
//               (services/corrido-v2/errors.js CODES)
// Each code has a line under corrido.stages.* / corrido.errors.* in BOTH
// en.json and es.json, so a Spanish customer never reads an English engine
// sentence during the longest waits. Anything the tables do not know falls back
// to the server's own message, then to a plain "job failed" line — never to the
// raw code.

const STAGE_CODE = /^[a-z][a-z0-9_]*$/;
const ERROR_CODE = /^E_[A-Z0-9_]+$/;
const SNAKE_CODE = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

type StatusLike = {
  stage?: string | null;
  message?: string | null;
  error?: string | null;
  error_code?: string | null;
};

/** The one progress line to show for a status frame. */
export function stageText(t: TFunction, s: StatusLike): string {
  const stage = typeof s.stage === "string" ? s.stage.trim() : "";
  if (stage && STAGE_CODE.test(stage)) {
    const key = `corrido.stages.${stage}`;
    const hit = t(key, { defaultValue: "" });
    if (hit) return hit;
  }
  return s.message || "";
}

/** The code a failure carries, if any: `error_code` first, then an `error`
 *  field that is itself a code (legacy rows put the code where the sentence goes). */
export function errorCodeOf(s: StatusLike): string {
  const explicit = typeof s.error_code === "string" ? s.error_code.trim() : "";
  if (explicit && ERROR_CODE.test(explicit)) return explicit;
  const err = typeof s.error === "string" ? s.error.trim() : "";
  if (err && ERROR_CODE.test(err)) return err;
  return "";
}

/** The one line to show for a failed frame or a refused mutation. */
export function errorText(t: TFunction, s: StatusLike, fallbackKey = "corrido.jobFailed"): string {
  const code = errorCodeOf(s);
  if (code) {
    const hit = t(`corrido.errors.${code}`, { defaultValue: "" });
    if (hit) return hit;
  }
  const err = typeof s.error === "string" ? s.error.trim() : "";
  // A snake_case refusal code (facelock_required, …): its own line when the
  // page has one, else the server's sentence; never the bare code.
  if (err && SNAKE_CODE.test(err)) {
    const hit = t(`corrido.errors.${err}`, { defaultValue: "" });
    if (hit) return hit;
    if (s.message) return s.message;
    return t(fallbackKey);
  }
  if (err && !ERROR_CODE.test(err)) return err;
  if (s.message) return s.message;
  return t(fallbackKey);
}
